import { DOC_GENERATOR_SOCKET, ERROR, EXCEL, PDF, SUCCESS } from './costBenchmarkTexts.js';
import * as d3 from 'd3';
import React from 'react';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';
import { PROGRAM } from '../../static/keyWords.js';
import { PLURAL_FORM_ALIAS } from '../NameConvention/nameConventionHelpers.js';
import { messageManager } from '../System/messageManager/messageManager.js';

export const CHART_POSITION = { top: 20, right: 40, bottom: 80, left: 80 };
export const FRAME_COLOR = 'var(--forge-theme-outline)';

export const getChartScales = (data, width, height) => {
  const xScale = d3.scaleLinear()
    .domain([0, d3.max(data, d => d.peerMedian) * 1.1 || 1])
    .range([0, width])
    .nice();

  const yScale = d3.scaleLinear()
    .domain([0, d3.max(data, d => d.costPerCapita) * 1.1 || 1])
    .range([height, 0])
    .nice();

  return { xScale, yScale };
};

export const getTooltipContent = (d, nameConventions) => {
  return <div className="tooltip-content">
    <span>{d.program}</span>
    <span>Your cost per capita: ${d.costPerCapita.toFixed(2)}</span>
    <span>Peer Median: ${d.peerMedian.toFixed(2)}</span>
    <span>Total cost: ${d.totalCost.toFixed(2)}</span>
    <span>Similar {nameConventions?.[PROGRAM]?.[PLURAL_FORM_ALIAS]}: {d.peer_count}</span>
    <span>Classification: {d.classification} ({d.std_dev.toFixed(2)} Std Dev)</span>
  </div>;
};

export const getCostBenchmarkHeaderButtons = (toggleBoundaries, onExport, disableExport, disabledButtonTooltip) => {
  return [
    {
      id: 'export',
      name: 'Export',
      variant: 'text',
      icon: 'export',
      type: 'dropDown',
      popOverIcon: true,
      children: EXPORT_COST_BENCHMARK_OPTIONS,
      onClickFunction: (e) => onExport(e?.detail?.value || ''),
      disabled: disableExport,
      tooltip: disabledButtonTooltip
    },
    {
      id: 'boundaries',
      name: 'Boundaries',
      variant: 'text',
      icon: 'chart_scatter_plot',
      onClickFunction: () => toggleBoundaries(),
      disabled: false,
    }
  ];
};

export const EXPORT_COST_BENCHMARK_OPTIONS = [
  {
    label: 'As Excel',
    value: EXCEL,
  },
  {
    label: 'As PDF',
    value: PDF,
  },
];

export const getIndicatorColor = (type) => {
  switch (type) {
    case SUCCESS: return 'success-color-background';
    case ERROR: return 'invalid-color-background';
    default: return 'default-color-background';
  }
};

export const getCircleColor = (type) => {
  switch (type) {
    case 'High': return 'dot-above';
    case 'Low': return 'dot-below';
    default: return 'dot';
  }
};

export const getTabButtons = (nameConventions) => {
  return [{ name: 'Graphic' }, { name: `${nameConventions?.[PROGRAM]?.[PLURAL_FORM_ALIAS]} match` }, {name: 'AI Insights'}];
};

export const INITIAL_FILTERS_STATE = { populationMin: 7000, populationMax: 10000 };
export const INITIAL_MATCH_SETTINGS_STATE = { threshold: 0.50 };

const DEFAULT_FILTERS = {
  filters: { ...INITIAL_FILTERS_STATE },
  matchSettings: { ...INITIAL_MATCH_SETTINGS_STATE },
};

export const buildFilters = (filters) => {
  const baseFilters = filters || DEFAULT_FILTERS;

  return {
    ...baseFilters,
    useStandardDeviation: false,
    includeAiInsights: false,
  };
};

export const handleFilterData = (data, filters) => {
  if (!data) return [];
  if (!filters) return data;
  let _filteredData = [...data];

  filters.forEach((filter) => {
    switch (filter.type) {
      case FILTER_TYPES.COST_BENCHMARK_COST: {
        const selectedNames = filter.value.map(v => v.name);

        _filteredData = _filteredData.filter(item =>
          selectedNames.includes(item.classification)
        );
        break;
      }
      case FILTER_TYPES.DEPARTMENT: {
        const selectedNames = filter.value.map(v => v.name);

        _filteredData = _filteredData.filter(item =>
          selectedNames.includes(item.department)
        );
        break;
      }
      default:
        break;
    }
  });

  return _filteredData;
};


export const removeUnusedKeysFromPrograms = (data) => {
  const cleanedData = data.map(item => {
    const newItem = { ...item };
    delete newItem.program_similarities;
    delete newItem.department_similarities;
    return newItem;
  });

  return { cleanedData };
};

export const onExportPdf = (cleanedData, user, matchSettings, organizationPopulation, socket) => {
  const container = document.getElementById('export-chart-container');
  const svgElement = container ? container.querySelector('svg') : null;
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(svgElement);

  svgString = svgString.replace(/var\(--forge-theme-outline\)/g, '#e0e0e0');
  svgString = svgString.replace(/var\(--forge-theme-success\)/g, '#2e7d32');
  svgString = svgString.replace(/var\(--forge-theme-error\)/g, '#b00020');
  svgString = svgString.replace(/var\(--forge-theme-primary\)/g, '#3f51b5');

  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();

  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = svgElement.clientWidth * 2;
    canvas.height = svgElement.clientHeight * 2;

    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(img, 0, 0);

    const base64Image = canvas.toDataURL('image/png');
    const payload = {
      benchmarkData: cleanedData,
      user,
      matchSettings,
      organizationPopulation,
      chartImage: base64Image,
    };

    socket.emit(DOC_GENERATOR_SOCKET.EXPORT_COST_BENCHMARK_DATA_PDF, payload);
    URL.revokeObjectURL(url);
  };

  img.src = url;
};


export const onExportExcel = (cleanedData, user, matchSettings, populationBoundaries, socket) => {
  const payload = {
    benchmarkData: cleanedData,
    user,
    matchSettings,
    populationBoundaries,
  };

  socket.emit(DOC_GENERATOR_SOCKET.EXPORT_COST_BENCHMARK_DATA_EXCEL, payload);

};

export const generateEmptyStateText = (nameConventions, population, hasPrograms) => {
  const emptyStateMessages = messageManager(nameConventions).COST_BENCHMARK.EMPTY_STATE;

  if (!population) return emptyStateMessages.MISSING_POPULATION;
  if (!hasPrograms) return emptyStateMessages.NO_PROGRAMS;

  return emptyStateMessages.TITLE;
};

export const getDisabledButtonTooltip = (isGeneratingDocument, isDataEmpty) => {
  const messages = messageManager();
  if (isGeneratingDocument) return messages.COST_BENCHMARK.DISABLED_BUTTON.GENERATING_DOCUMENT;
  if (isDataEmpty) return messages.COST_BENCHMARK.DISABLED_BUTTON.NO_DATA;
  return '';
};
